import { useEffect, useState, useRef } from "react";
import { Bell, X, CheckCircle2, AlertTriangle, Info, XCircle, Circle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";

const SEV_ICON = { info: Info, warning: AlertTriangle, error: XCircle, success: CheckCircle2 };
const SEV_COLOR = {
  info: "text-sky-600 bg-sky-50",
  warning: "text-amber-700 bg-amber-50",
  error: "text-red-700 bg-red-50",
  success: "text-emerald-700 bg-emerald-50",
};

const POLL_MS = 60000;

export default function NotificationBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  async function pollCount() {
    try {
      const r = await api.notifUnreadCount();
      setCount(r?.unread || 0);
    } catch {}
  }

  async function loadItems() {
    setLoading(true);
    try {
      const r = await api.notifList({ limit: 30 });
      setItems(r || []);
    } catch (e) {
      toast.error("Impossibile caricare le notifiche");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    pollCount();
    const id = setInterval(pollCount, POLL_MS);
    const onFocus = () => pollCount();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, []);

  useEffect(() => {
    if (open) loadItems();
  }, [open]);

  useEffect(() => {
    function onDoc(e) {
      if (open && ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function markAllRead() {
    try {
      await api.notifReadAll();
      setCount(0);
      await loadItems();
      toast.success("Tutte segnate come lette");
    } catch {
      toast.error("Errore");
    }
  }

  async function onItemClick(n) {
    if (!n.read_at) {
      try { await api.notifRead(n.id); setCount((c) => Math.max(0, c - 1)); } catch {}
    }
    if (n.link_url) {
      setOpen(false);
      nav(n.link_url);
    }
  }

  return (
    <div className="relative" ref={ref} data-testid="notif-bell-container">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-xl hover:bg-white/70 transition"
        style={{ color: "var(--pxos-text-dark)" }}
        data-testid="notif-bell-btn"
        aria-label="Notifiche"
      >
        <Bell size={18} />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notif-bell-count"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-[340px] max-w-[calc(100vw-16px)] rounded-xl bg-white shadow-lg border z-50"
          style={{ borderColor: "var(--pxos-border)" }}
          data-testid="notif-panel"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b"
               style={{ borderColor: "var(--pxos-border)" }}>
            <div className="font-semibold text-sm" style={{ color: "var(--pxos-text-dark)" }}>Notifiche</div>
            <div className="flex items-center gap-2">
              {count > 0 && (
                <button className="text-xs opacity-70 hover:opacity-100"
                        onClick={markAllRead}
                        data-testid="notif-read-all">
                  Segna tutte lette
                </button>
              )}
              <button className="p-1 hover:bg-slate-100 rounded"
                      onClick={() => setOpen(false)}
                      aria-label="Chiudi">
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="max-h-[420px] overflow-y-auto" data-testid="notif-list">
            {loading && <div className="p-4 text-sm opacity-70 text-center">Caricamento…</div>}
            {!loading && items.length === 0 && (
              <div className="p-6 text-center opacity-70 text-sm" data-testid="notif-empty">
                Nessuna notifica.
              </div>
            )}
            {!loading && items.map((n) => {
              const Icon = SEV_ICON[n.severity] || Circle;
              const color = SEV_COLOR[n.severity] || "text-slate-600 bg-slate-50";
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => onItemClick(n)}
                  className={[
                    "w-full text-left px-3 py-2 border-b hover:bg-slate-50 transition block",
                    !n.read_at ? "bg-white" : "opacity-70",
                  ].join(" ")}
                  style={{ borderColor: "var(--pxos-border)" }}
                  data-testid={`notif-item-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${color}`}>
                      <Icon size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">
                        {n.title}
                        {!n.read_at && <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-sky-500" />}
                      </div>
                      {n.body && <div className="text-xs opacity-70 line-clamp-2">{n.body}</div>}
                      <div className="text-[10px] opacity-50 mt-0.5">
                        {new Date(n.created_at).toLocaleString("it-IT")}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
